import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiInfo, FiTrendingUp, FiShield, FiAlertTriangle, FiDroplet } = FiIcons;

const SEOContent = ({ weatherData }) => {
  const currentTemp = weatherData?.regional?.mindanao?.temperature || '25-30°C';
  const currentCondition = weatherData?.regional?.mindanao?.condition || 'Partly cloudy';

  return (
    <motion.article 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      transition={{ delay: 0.5 }}
      className="bg-white rounded-xl shadow-lg p-6 border border-blue-100 mt-6"
    >
      <header className="mb-8 border-b border-gray-100 pb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <SafeIcon icon={FiInfo} className="mr-2 text-blue-600" />
          Iligan City Weather & Disaster Preparedness Guide
        </h2>
        <p className="text-gray-600">
          The definitive resource for weather monitoring in the "City of Majestic Waterfalls."
        </p>
      </header>

      <div className="grid md:grid-cols-2 gap-8">
        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiMapPin} className="mr-2 text-blue-500" />
            Geography & Climate Focus
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            Iligan City (8.2280°N, 124.2452°E) is uniquely positioned between <strong>Iligan Bay</strong> and the highlands of <strong>Lanao del Norte</strong>. This topography creates micro-climates where coastal barangays like <em>Santiago</em> experience strong sea breezes, while highland areas like <em>Saray</em> are significantly cooler.
          </p>
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
            <h4 className="font-bold text-blue-900 text-sm mb-2">Key Monitoring Zones:</h4>
            <ul className="text-sm text-blue-800 space-y-2">
              <li><strong>Mandulog River System:</strong> Critical for flood monitoring in Tubod and Pala-o.</li>
              <li><strong>Iligan Bay:</strong> Monitored for storm surge risks affecting the port Area.</li>
              <li><strong>Central Business District:</strong> Focus on urban heat and drainage efficiency.</li>
            </ul>
          </div>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <SafeIcon icon={FiShield} className="mr-2 text-green-500" />
            Typhoon Safety Protocols
          </h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            During a typhoon, Northern Mindanao often serves as a pathway for storms entering from the Philippine Sea. Our tracker uses <strong>PAGASA Public Storm Warning Signals</strong> (PSWS) as the primary authority for local action.
          </p>
          <div className="space-y-3">
            <div className="flex items-start p-3 bg-red-50 rounded-lg border border-red-100">
              <SafeIcon icon={FiAlertTriangle} className="text-red-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-red-900">Flash Flood Alert</p>
                <p className="text-xs text-red-700">Heed Mandulog River warnings. Flash floods can occur within 15-30 minutes of heavy upland rainfall.</p>
              </div>
            </div>
            <div className="flex items-start p-3 bg-orange-50 rounded-lg border border-orange-100">
              <SafeIcon icon={FiDroplet} className="text-orange-500 mt-1 mr-3 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-orange-900">Storm Surge Notice</p>
                <p className="text-xs text-orange-700">Coastal residents in Santiago and Tipanoy should monitor high tide schedules during typhoons.</p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <footer className="mt-8 pt-6 border-t border-gray-100">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Quick Weather Reference</h3>
        <div className="flex flex-wrap gap-3">
          {[
            '#IliganWeather', '#MandulogRiver', '#MindanaoTyphoon', 
            '#PAGASAUpdates', '#IliganCityDRRMO', '#SafeIligan',
            '#LanaoDelNorteWeather', '#NorthernMindanaoAlert'
          ].map((tag) => (
            <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium hover:bg-blue-100 hover:text-blue-700 transition-colors cursor-default">
              {tag}
            </span>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-6 text-center">
          Monitoring Station: Iligan City Center | Data Source: PAGASA SYNOTIC | Lat: 8.2280 Lon: 124.2452
        </p>
      </footer>
    </motion.article>
  );
};

export default SEOContent;